// src/components/UmatUpdateApproval.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../utils/wkCommon";
import { getUser } from "../utils/auth";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import PERMISSIONS from "../utils/wkCommon";

const rem = px => `${px / 16}rem`;

function getUmatDisplayLabel(u) {
  if (u?.chineseName && u?.fullName) {
    return `${u.chineseName} ${u.fullName}`;
  }
  if (u?.chineseName && !u?.fullName && u?.pinyinName) {
    return `${u.chineseName} ${u.pinyinName}`;
  }
  if (u?.chineseName) return u.chineseName;
  if (u?.pinyinName) return u.pinyinName;
  if (u?.nickName) return u.nickName;
  return u?.umatID ?? "—";
}

export default function UmatUpdateApproval() {
  const user = getUser();
  const [canEdit, setCanEdit] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const filterUsername = (!canApprove && canEdit)
      ? (user?.username ?? "(empty)")
      : null;

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false); // guard to prevent overlapping loads
  const [records, setRecords] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showProcessed, setShowProcessed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [actionState, setActionState] = useState("idle");
  // idle | processing | success | error

  // load perms (only depends on user)
  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      if (!user) {
        mounted && setCanEdit(false);
        mounted && setCanApprove(false);
        return;
      }
      try {
        const email = user.email;
        const pEdit = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDIT);
        const pApprove = await hasUmatPermissionAsync(email, PERMISSIONS.UMATDATA_EDITAPPROVAL);
        if (mounted) {
          setCanEdit(Boolean(pEdit));
          setCanApprove(Boolean(pApprove));
        }
      } catch (err) {
        console.warn("load perms error", err);
        if (mounted) { setCanEdit(false); setCanApprove(false); }
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [user]);

  // fetch records: only when refreshKey or user.id changes (stable)
  useEffect(() => {
    let cancelled = false;
    // prevent overlapping fetch calls
    if (loadingRef.current) return;

    async function load() {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      try {
        // fetch all umat-updates with populated targetUmats, limited page size
        const path = `/api/umat-updates?populate=targetUmats&pagination[page]=1&pagination[pageSize]=200`;
        const json = await apiFetch(path);

        if (cancelled) return;

        let arr = json?.data ?? [];

        // If user has only edit (no approval) show only their records
        /*
        if (canEdit && !canApprove) {
          const uid = String(user?.username ?? "");
          arr = arr.filter(r => {
            const owner = r.recordOwner;
            if (owner === undefined || owner === null) return false;
            return String(owner) === uid;
          });
        }
        */

        setRecords(arr);

        // === NEW: resolve relation labels ===
        const ids = collectRelationIds(arr);
        Promise.all([
          fetchAreasByIds(ids.area),
          fetchQiuByIds(ids.qiu),
          fetchUmatsByIds(ids.umat),
          fetchRelationsByIds(ids.relation)
        ]).then(([areaMap, qiuMap, umatMap, relationMap]) => {
          setRelationLabels({
            area: areaMap,
            qiu: qiuMap,
            umat: umatMap,
            relation: relationMap
          });
        });
      } catch (err) {
        console.error("load umat-updates error", err);
        // Set an error but DO NOT trigger automatic re-fetch -> prevents blink loop
        setError("Gagal memuat daftar perubahan: " + (err?.message ?? String(err)));
        setRecords([]); // keep empty list so UI stable
      } finally {
        if (!cancelled) setLoading(false);
        loadingRef.current = false;
      }
    }

    load();
    return () => { cancelled = true; };
    // note: do not include canEdit/canApprove to avoid retrigger loop when perms update
  }, [refreshKey, user?.username]); // minimal deps; including canApprove/canEdit only to ensure visibility correct

  const visibleRecords = useMemo(() => {
    if (!records.length) return [];

    // approver lihat semua
    if (canApprove) return records;

    // editor hanya lihat miliknya
    if (canEdit && !canApprove) {
      const uid = String(user?.username ?? "");
      return records.filter(r => String(r.recordOwner) === uid);
    }

    return [];
  }, [records, canEdit, canApprove, user?.username]);

  // Sorting and filtering same as before
  const sortedRecords = useMemo(() => {
    const priority = { Pending: 0, Approved: 1, Applied: 2, Rejected: 3, default: 4 };
    const copy = (records || []).slice();
    copy.sort((a, b) => {
      const sa = a.updateStatus ?? "default";
      const sb = b.updateStatus ?? "default";
      const pa = priority[sa] ?? priority.default;
      const pb = priority[sb] ?? priority.default;
      if (pa !== pb) return pa - pb;
      const ca = new Date(a.createdAt ?? a.updatedAt ?? 0).getTime();
      const cb = new Date(b.createdAt ?? b.updatedAt ?? 0).getTime();
      return cb - ca;
    });
    if (!showProcessed) {
      return copy.filter(r => {
        const s = r.updateStatus ?? "";
        return !(s === "Applied" || s === "Rejected");
      });
    }
    return copy;
  }, [records, showProcessed]);

  function toggleSelect(recordId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(recordId)) next.delete(recordId); else next.add(recordId);
      return next;
    });
  }

  function extractTargetUmats(record) {
    const t = record?.targetUmats;
    if (!t) return [];
    const arr = Array.isArray(t?.data) ? t.data : t;
    return (arr || []).map(u => ({
      ...u,
      __docId: u.documentId
    }));
  }

  function collectRelationIds(records) {
    const area = new Set();
    const qiu = new Set();
    const umat = new Set();
    const relation = new Set();

    records.forEach(r => {
      const p = r.updatePayload || {};
      if (p.area) area.add(p.area);
      if (p.qiu_dao_person) qiu.add(p.qiu_dao_person);
      if (p.father_umat_item) umat.add(p.father_umat_item);
      if (p.mother_umat_item) umat.add(p.mother_umat_item);
      if (p.relation_contact_preference) relation.add(p.relation_contact_preference);
    });

    return {
      area: [...area],
      qiu: [...qiu],
      umat: [...umat],
      relation: [...relation]
    };
  }

  async function fetchAreasByIds(ids) {
    if (!ids.length) return {};
    const qs = ids.map(id => `filters[id][$in]=${id}`).join("&");
    const res = await apiFetch(`/api/areas?${qs}`);
    const map = {};
    (res?.data || []).forEach(a => { map[a.id] = a.name; });
    return map;
  }

  async function fetchQiuByIds(ids) {
    if (!ids.length) return {};
    const qs = ids.map(id => `filters[id][$in]=${id}`).join("&");
    const res = await apiFetch(`/api/qiu-dao-people?${qs}`);
    const map = {};
    (res?.data || []).forEach(q => { map[q.id] = q.qiuDaoName; });
    return map;
  }

  async function fetchUmatsByIds(ids) {
    if (!ids.length) return {};
    const qs = ids.map(id => `filters[id][$in]=${id}`).join("&");
    const res = await apiFetch(`/api/umats?${qs}`);
    const map = {};
    (res?.data || []).forEach(u => { map[u.id] = getUmatDisplayLabel(u); });
    return map;
  }

  async function fetchRelationsByIds(ids) {
    if (!ids.length) return {};
    const qs = ids.map(id => `filters[id][$in]=${id}`).join("&");
    const res = await apiFetch(`/api/umat-relations?${qs}&populate=umat_related_to`);
    const map = {};
    (res?.data || []).forEach(r => {
      map[r.id] = r.umat_related_to
        ? getUmatDisplayLabel(r.umat_related_to)
        : "—";
    });
    return map;
  }

  async function updateUmatUpdateRecordStatus(recordId, bodyPatch = {}) {
    try {
      await apiFetch(`/api/umat-updates/${recordId}`, { method: "PUT", body: { data: bodyPatch } });
      return { success: true };
    } catch (err) {
      console.error("failed update umat-updates", err);
      return { success: false, error: err?.message ?? String(err) };
    }
  }

  async function processRecordApply(record) {
    const attrs = record.attributes ?? {};
    const type = attrs.type ?? "single";
    const payload = attrs.updatePayload ?? {};

    async function putUmat(docId, data) {
      try {
        const resp = await apiFetch(`/api/umats/${docId}`, { method: "PUT", body: { data } });
        if (!resp || resp.error) {
          throw new Error("Invalid response");
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err?.message ?? String(err) };
      }
    }

    try {
      if (type === "single") {
        const docIds = extractTargetUmats(record);
        if (!docIds || docIds.length === 0) return { success: false, detail: "No target umat specified" };
        const docId = docIds[0];
        const res = await putUmat(docId, payload);
        return res;
      }

      if (type === "bulk") {
        const ids = extractTargetUmats(record);
        if (!ids || ids.length === 0) return { success: false, detail: "No target umats for bulk" };
        const results = [];
        for (const id of ids) {
          /* eslint-disable no-await-in-loop */
          const r = await putUmat(id, payload);
          results.push({ id, ...r });
        }
        const anyFail = results.some(r => !r.success);
        return { success: !anyFail, detail: results };
      }

      if (type === "mappingQiuDao") {
        const targets = extractTargetUmats(record);
        if (!targets || targets.length === 0) {
          return { success: false, detail: "No target umat" };
        }
        const qid = (payload && payload.qiu_dao_person) ? payload.qiu_dao_person : null;
        if (!qid) return { success: false, detail: "No qiu_dao_person id in payload" };
        const results = [];
        for (const u of targets) {
          const docId = u.documentId;
          if (!docId) continue;
          const r = await putUmat(docId, { qiu_dao_person: qid });
          results.push({ id: docId, ...r });
        }
        const anyFail = results.some(r => !r.success);
        return { success: !anyFail, detail: results };
      }

      return { success: false, detail: `Unknown update type: ${type}` };
    } catch (err) {
      return { success: false, detail: err?.message ?? String(err) };
    }
  }

  async function handleApproveSelected() {
    if (loadingRef.current) return;
    if (!canApprove) {
      setError("Anda tidak memiliki izin untuk approve.");
      return;
    }
    if (selectedIds.size === 0) {
      setError("Pilih minimal satu record untuk di-approve.");
      return;
    }
    setActionState("processing");
    setError(null);
    setInfo(null);
    setLoading(true);

    const ids = Array.from(selectedIds);
    const resultsSummary = [];

    for (const idStr of ids) {
      try {
        const record = records.find(r => String(r.id ?? r.documentId) === String(idStr));
        if (!record) {
          resultsSummary.push({ id: idStr, success: false, error: "Record tidak ditemukan di cache" });
          continue;
        }
        const recId = record.documentId;
        const approver = user?.username ?? null;
        const markApproved = await updateUmatUpdateRecordStatus(recId, {
          updateStatus: "Approved",
          recordApprover: approver
        });
        if (!markApproved.success) {
          resultsSummary.push({ id: recId, success: false, error: "Gagal set Approved: " + (markApproved.error ?? "unknown") });
          continue;
        }

        const applyResult = await processRecordApply(record);

        const finalStatus = applyResult.success ? "Applied" : "Approved";
        const appliedPayload = applyResult.detail ?? null;
        const updateResp = await updateUmatUpdateRecordStatus(recId, {
          updateStatus: finalStatus,
          appliedStatus: appliedPayload ? appliedPayload : (applyResult.success ? { ok: true } : { ok: false, detail: applyResult.detail }),
          recordApprover: approver
        });

        if (!updateResp.success) {
          resultsSummary.push({ id: recId, success: false, error: "Apply done but failed update record: " + (updateResp.error ?? "unknown") });
        } else {
          resultsSummary.push({ id: recId, success: applyResult.success, detail: applyResult.detail });
        }
      } catch (err) {
        resultsSummary.push({ id: idStr, success: false, error: err?.message ?? String(err) });
      }
    }

    setLoading(false);
    setSelectedIds(new Set());
    setRefreshKey(k => k + 1);

    const succ = resultsSummary.filter(r => r.success).length;
    const fail = resultsSummary.length - succ;

    setActionState(fail > 0 ? "error" : "success");
    setInfo(`Approve selesai. Sukses: ${succ}, Gagal: ${fail}.`);
    setError(fail > 0 ? `Beberapa record gagal diproses. Lihat console atau detail appliedStatus pada tiap record.` : null);
    console.info("approve summary", resultsSummary);
  }

  async function handleRejectSelected() {
    if (loadingRef.current) return;
    if (!canApprove) {
      setError("Anda tidak memiliki izin untuk reject.");
      return;
    }
    if (selectedIds.size === 0) {
      setError("Pilih minimal satu record untuk di-reject.");
      return;
    }
    // avoid global confirm; use window.confirm to satisfy ESLint
    if (!window.confirm("Yakin ingin menolak (reject) record terpilih? Tindakan ini tidak dapat dibatalkan.")) return;

    setLoading(true);
    setActionState("processing");

    const ids = Array.from(selectedIds);
    const results = [];
    for (const idStr of ids) {
      try {
        const record = records.find(r => String(r.id ?? r.documentId) === String(idStr));
        if (!record) { results.push({ id: idStr, success: false, error: "Record tidak ditemukan" }); continue; }
        const recId = record.id ?? record.documentId;
        const approver = user?.username ?? user?.username ?? user?.email ?? null;
        const resp = await updateUmatUpdateRecordStatus(recId, {
          updateStatus: "Rejected",
          recordApprover: approver,
          notes: (record.notes ? record.notes + " | " : "") + `Rejected by ${approver} at ${new Date().toISOString()}`
        });
        results.push({ id: recId, success: resp.success, error: resp.error });
      } catch (err) {
        results.push({ id: idStr, success: false, error: err?.message ?? String(err) });
      }
    }
    setLoading(false);
    setSelectedIds(new Set());
    setRefreshKey(k => k + 1);
    const ok = results.filter(r => r.success).length;
    const fail = results.length - ok;
    setActionState(fail > 0 ? "error" : "success");
    setInfo(`Reject selesai. Berhasil: ${ok}, Gagal: ${fail}.`);
    setError(fail > 0 ? "Beberapa update gagal di-reject. Cek konsol atau backend logs." : null);
  }

  const [relationLabels, setRelationLabels] = useState({
    area: {},
    qiu: {},
    umat: {},
    relation: {}
  });

  function renderValue(field, value) {
    if (value == null) return "—";
    switch (field) {
      case "area":
        return relationLabels.area[value] ?? `#${value}`;
      case "qiu_dao_person":
        return relationLabels.qiu[value] ?? `#${value}`;
      case "father_umat_item":
      case "mother_umat_item":
        return relationLabels.umat[value] ?? `#${value}`;
      case "relation_contact_preference":
        return relationLabels.relation[value] ?? `#${value}`;
      default:
        return String(value);
    }
  }

  function renderChanges(record) {
    const payload = record.updatePayload || {};
    const original = record.originalValues || {};

    return Object.keys(payload).map(field => (
      <div key={field} style={{ marginBottom: 6 }}>
        <strong>{field}</strong>
        <div style={{ fontSize: 13, color: "#555" }}>
          from: {renderValue(field, original[field])} <br />
          to:   {renderValue(field, payload[field])}
        </div>
      </div>
    ));
  }

  function RecordRow({ record }) {
    const [showDetail, setShowDetail] = useState(false);

    const id = record.id ?? record.documentId;
    const attrs = record ?? {};
    const status = attrs.updateStatus ?? "Pending";
    const owners = attrs.recordOwner ?? "";
    const createdAt = attrs.createdAt ? new Date(attrs.createdAt).toLocaleString() : (attrs.updatedAt ? new Date(attrs.updatedAt).toLocaleString() : "");
    const targets = extractTargetUmats(record);
    const title = `${attrs.type ?? "?"} — ${attrs.notes ?? ""}`;
    const checked = selectedIds.has(String(id));

    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: canApprove ? "32px 1fr 120px 160px 120px" : "1fr 120px 160px 120px",
        gap: rem(8),
        padding: rem(10),
        borderBottom: "1px solid #edf2f7",
        alignItems: "center",
        background: status === "Pending" ? "#fffef8" : (status === "Approved" ? "#f7fbff" : "white"),
        fontSize: rem(14)
      }}>
        {canApprove && (
          <div>
            <input type="checkbox" checked={checked} onChange={() => toggleSelect(String(id))} />
          </div>
        )}

        <div>
          <div style={{ fontWeight: 700 }}>{attrs.type ?? "—"} {attrs.updatePayload && <span style={{ marginLeft: rem(6), color: "#6b7280", fontWeight: 500 }}>({attrs.updatePayload && Object.keys(attrs.updatePayload).length} field)</span>}</div>
          <div style={{ fontSize: rem(13), color: "#444" }}>{title}</div>
          <div style={{ marginTop: rem(6), fontSize: rem(13), color: "#6b7280" }}>
            Targets:{" "}
            <span title={targets.map(u => getUmatDisplayLabel(u)).join("\n")}>
              {targets.slice(0, 3).map(u => getUmatDisplayLabel(u)).join(", ")}
              {targets.length > 3 && `, +${targets.length - 3}`}
            </span>
          </div>
          {/* ⬇️ INI tombol toggle */}
          <button
            type="button"
            onClick={() => setShowDetail(v => !v)}
            style={{
              marginTop: rem(4),
              fontSize: rem(12),
              color: "#2b4a84",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0
            }}
          >
            {showDetail ? "▲ Sembunyikan detail" : "▼ Lihat detail perubahan"}
          </button>

          {/* ⬇️ INI detail perubahan */}
          {showDetail && (
            <div
              style={{
                marginTop: rem(6),
                padding: rem(8),
                background: "#f8fafc",
                border: "1px dashed #d1d5db",
                borderRadius: rem(6)
              }}
            >
              {renderChanges(record)}
            </div>
          )}
        </div>

        <div style={{ fontSize: rem(13) }}>
          <div style={{ fontWeight: 700 }}>{owners}</div>
          <div style={{ color: "#6b7280", marginTop: rem(4) }}>{createdAt}</div>
        </div>

        <div style={{ fontSize: rem(13) }}>
          <div style={{ fontWeight: 700 }}>{status}</div>
          <div style={{ color: "#6b7280", marginTop: rem(4) }}>{attrs.recordApprover ?? "—"}</div>
        </div>

        <div style={{ fontSize: rem(13) }}>
          <div style={{ fontWeight: 700 }}>Applied status</div>
          <div style={{ color: "#6b7280", marginTop: rem(4), maxWidth: rem(28), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {attrs.appliedStatus ? JSON.stringify(attrs.appliedStatus) : "—"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: rem(14) }}>
      <h3 style={{ marginBottom: rem(8) }}>Approval — Umat Updates</h3>

      <div style={{ marginBottom: rem(8), display: "flex", gap: rem(8), alignItems: "center" }}>
        <div style={{ color: "#444" }}>Role: {canApprove ? "Approver (UMATDATA_EDITAPPROVAL)" : (canEdit ? "Editor (UMATDATA_EDIT)" : "No access")}</div>
        
        <div style={{ fontSize: rem(13), color: "#6b7280" }}>
          Filter owner (username):{" "}
          <strong>
            {filterUsername ?? "—"}
          </strong>
        </div>
        
        <div style={{ marginLeft: "auto", display: "flex", gap: rem(8), alignItems: "center" }}>
          <label style={{ fontSize: rem(13), color: "#444" }}>
            <input type="checkbox" checked={showProcessed} onChange={(e) => setShowProcessed(e.target.checked)} /> Show processed (Applied / Rejected)
          </label>
          <button onClick={() => { setRefreshKey(k => k + 1); }} style={{ padding: rem(8), borderRadius: rem(6), border: "1px solid #d1d5db", background: "#fff" }}>Refresh</button>
        </div>
      </div>

      {loading && <div style={{ color: "#666", marginBottom: rem(8) }}>Memuat daftar...</div>}
      {error && <div style={{ color: "crimson", marginBottom: rem(8) }}>{error}</div>}
      {info && <div style={{ background: "#e6f4ea", color: "#0f5132", padding: rem(8), borderRadius: rem(6), marginBottom: rem(8) }}>{info}</div>}

      <div style={{ border: "1px solid #e6eef8", borderRadius: rem(8), overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: canApprove ? "32px 1fr 120px 160px 120px" : "1fr 120px 160px 120px", gap: rem(8), padding: rem(10), background: "#f8fafc", fontWeight: 700 }}>
          {canApprove && <div></div>}
          <div>Summary</div>
          <div>Owner</div>
          <div>Status / Approver</div>
          <div>Applied Status</div>
        </div>

        <div>
          {sortedRecords.length === 0 && <div style={{ padding: rem(12), color: "#666" }}>Tidak ada request.</div>}
          {sortedRecords.map(rec => <RecordRow key={rec.id ?? rec.documentId} record={rec} />)}
        </div>
      </div>

      {canApprove && (
        <div style={{ marginTop: rem(10), display: "flex", gap: rem(8), justifyContent: "flex-end" }}>
          <button onClick={() => { setSelectedIds(new Set()); }} style={{ padding: rem(8), borderRadius: rem(6), border: "1px solid #d1d5db", background: "#fff" }}>Clear selection</button>
          <button 
            onClick={handleRejectSelected} 
            disabled={loading || actionState === "processing" || selectedIds.size === 0} 
            style={{ padding: rem(8), borderRadius: rem(6), border: "1px solid #f5c2c7", background: "#fff3f2", color: "#842029" }}
          >
            {actionState === "processing"
              ? "Processing..."
              : actionState === "success"
              ? "Done"
              : "Reject"}
            </button>
          <button
            onClick={handleApproveSelected}
            disabled={
              loading ||
              actionState === "processing" ||
              selectedIds.size === 0
            }
            style={{ 
              padding: rem(8), 
              borderRadius: rem(6), 
              border: "none", 
              background:
                actionState === "success"
                  ? "#16a34a"
                  : "#2b4a84",
              cursor:
                actionState === "processing"
                  ? "wait"
                  : "pointer"
            }}
          >
            {actionState === "processing"
              ? "Processing..."
              : actionState === "success"
              ? "Done"
              : "Approve & Process"}
          </button>
        </div>
      )}

      {!canApprove && canEdit && (
        <div style={{ marginTop: rem(10), color: "#6b7280" }}>Anda melihat request milik Anda. Hubungi approver untuk memprosesnya.</div>
      )}

      {visibleRecords.length === 0 && (
        <div style={{ padding: rem(12), color: "#666" }}>
          Tidak ada request.
        </div>
      )}
    </div>
  );
}
